import ReactMarkdown from "react-markdown"
import { API } from "../../../api/api"
import { motion } from "framer-motion"
import { useEffect, useMemo, useState } from "react"

type ProjectDetailTextSectionType = {
    description: string
    feedback?: string
}

function markdownToPlainText(markdown: string): string {
    return markdown
        .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/`{1,3}[^`]*`{1,3}/g, "")
        .replace(/(^|\n)#{1,6}\s+/g, "$1")
        .replace(/(^|\n)>\s?/g, "$1")
        .replace(/(^|\n)[*-+]\s+/g, "$1")
        .replace(/(^|\n)\d+\.\s+/g, "$1")
        .replace(/[*_~]/g, "")
        .replace(/\r?\n+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
}

export default function TextSection(props: ProjectDetailTextSectionType) {
    const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false)

    const feedbackPlainText = useMemo(() => {
        if (!props.feedback) return ""
        return markdownToPlainText(props.feedback)
    }, [props.feedback])

    const feedbackWords = useMemo(() => {
        if (!feedbackPlainText) return []
        return feedbackPlainText.split(/\s+/)
    }, [feedbackPlainText])

    const isFeedbackLong = feedbackWords.length > 70

    const displayedFeedbackPreview = useMemo(() => {
        if (!feedbackPlainText) return ""
        if (!isFeedbackLong) return feedbackPlainText
        return `${feedbackWords.slice(0, 70).join(" ")}...`
    }, [feedbackPlainText, feedbackWords, isFeedbackLong])

    useEffect(() => {
        setIsFeedbackExpanded(false)
    }, [props.feedback])

    return (
        <section className={`flex flex-col w-full`}>
            <div className="flex flex-col lg:flex-row py-32 max-w-page mx-auto px-4 md:px-8 xl:px-0">
                <motion.div
                    className="w-full min-w-[350px] lg:pr-16 pb-6 lg:pb-0"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <h5 className="text-4xl truncate font-semibold font-headline ">Über das Projekt</h5>
                </motion.div>
                <motion.div
                    className="lg:pl-16 lg:border-l lg:border-base-300/70"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: 0.4 }}>
                    <div className="text-lg markdown-content !leading-loose">
                        <ReactMarkdown
                            components={{
                                p: ({ children }) => <p className="font-text opacity-75 text-md">{children}</p>,
                                img: ({ src, alt }) => (
                                    <img src={API.img + src} alt={alt} className="w-fit h-full max-h-96 object-cover rounded-default mx-auto" />
                                ),
                                a: ({ href, children }) => (
                                    <a href={href} className="text-primary underline">
                                        {children}
                                    </a>
                                ),
                                ul: ({ children }) => <ul className="list-disc list-inside">{children}</ul>,
                                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                h1: ({ children }) => <h1 className="text-3xl font-semibold font-headline pb-2">{children}</h1>,
                                h2: ({ children }) => <h2 className="text-2xl font-semibold font-headline pb-2">{children}</h2>,
                                h3: ({ children }) => <h3 className="text-xl font-semibold font-headline pb-1">{children}</h3>,
                                h4: ({ children }) => <h4 className="text-lg font-semibold font-headline pb-1">{children}</h4>,
                                h5: ({ children }) => <h5 className="text-base font-semibold font-headline pb-1">{children}</h5>,
                                h6: ({ children }) => <h6 className="text-sm font-semibold font-headline">{children}</h6>,
                            }}>
                            {props.description}
                        </ReactMarkdown>
                    </div>
                </motion.div>
            </div>
            {props.feedback && (
                <div className="py-32 bg-base-200">
                    <div className="w-full max-w-page mx-auto px-4 md:px-8 xl:px-0 flex flex-col lg:flex-row">

                        <motion.div
                        className="min-w-[350px]  pb-6 lg:pb-0"
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: 0.2 }}>
                            <h5 className="text-4xl font-semibold font-headline lg:pr-16">Kundenstimme</h5>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: 0.4 }}>
                            <div className=" text-lg !leading-loose markdown-content lg:pl-16 lg:border-l lg:border-base-300/70">
                                {isFeedbackLong && !isFeedbackExpanded ? (
                                    <p className="font-text opacity-75 text-md">{displayedFeedbackPreview}</p>
                                ) : (
                                    <ReactMarkdown
                                        components={{
                                            p: ({ children }) => <p className="font-text opacity-75 text-md">{children}</p>,
                                            img: ({ src, alt }) => (
                                                <img src={API.img + src} alt={alt} className="w-fit h-full max-h-96 object-cover rounded-default mx-auto" />
                                            ),
                                            a: ({ href, children }) => (
                                                <a href={href} className="text-primary underline">
                                                    {children}
                                                </a>
                                            ),
                                            ul: ({ children }) => <ul className="list-disc list-inside">{children}</ul>,
                                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                            h1: ({ children }) => <h1 className="text-3xl font-semibold font-headline pb-2">{children}</h1>,
                                            h2: ({ children }) => <h2 className="text-2xl font-semibold font-headline pb-2">{children}</h2>,
                                            h3: ({ children }) => <h3 className="text-xl font-semibold font-headline pb-1">{children}</h3>,
                                            h4: ({ children }) => <h4 className="text-lg font-semibold font-headline pb-1">{children}</h4>,
                                            h5: ({ children }) => <h5 className="text-base font-semibold font-headline pb-1">{children}</h5>,
                                            h6: ({ children }) => <h6 className="text-sm font-semibold font-headline">{children}</h6>,
                                        }}>
                                        {props.feedback}
                                    </ReactMarkdown>
                                )}
                                {isFeedbackLong && (
                                    <button
                                        type="button"
                                        className="mt-6 font-text text-primary underline"
                                        onClick={() => setIsFeedbackExpanded((prev) => !prev)}
                                        aria-expanded={isFeedbackExpanded}
                                    >
                                        {isFeedbackExpanded ? "weniger lesen" : "mehr lesen"}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>

                </div>
            )}
        </section>
    )
}
